import { useCallback, useEffect, useMemo, useState } from 'react';
import { bookingApi, BookingApiError } from '../../lib/api';
import { errorKeyFor, useT } from '../../lib/i18n';
import { formatDuration, todayInZone, useMoney } from '../../lib/format';
import { useDraft, useSession, useUi } from '../../lib/store';
import { buildSteps, nextStep, previousStep, type StepKey } from '../../lib/steps';
import { portalReturnUrl, type TenantRoute } from '../../lib/tenant';
import { ErrorNote, ScreenHeading, StepRail } from '../shell/PortalShell';
import { BranchStep } from './BranchStep';
import { ServicesStep } from './ServicesStep';
import { StaffStep } from './StaffStep';
import { ReviewStep } from './ReviewStep';
import { TimeStep, useVisibleRange } from './TimeStep';
import { BookingCard, calendarHref } from '../bookings/BookingCard';
import type { Availability, Booking, Catalog, DepositGatewayCode, Site, Slot, Slots, Staff } from '../../lib/types';

/**
 * The booking wizard.
 *
 * Steps are derived, not stored: which step you are on is a function of what
 * the draft contains. That makes the back button, the step rail and a page
 * refresh all behave without a separate state machine to keep in sync.
 *
 * Sign-in is deferred to the last possible moment — the customer picks a
 * location, services, staff and a time before being asked who they are, which
 * is the single biggest difference between a booking page people finish and one
 * they abandon.
 */

type Step = StepKey;

export function BookingFlow({
  site,
  slug,
  route,
  onBooked,
}: {
  site: Site;
  slug: string;
  route: TenantRoute;
  onBooked: (booking: Booking) => void;
}): JSX.Element {
  const { t, lang } = useT();
  const money = useMoney();
  const draft = useDraft();
  const account = useSession((s) => s.account);
  const setAuthOpen = useUi((s) => s.setAuthOpen);

  const singleBranch = site.branches.length === 1;
  const [step, setStep] = useState<Step>(singleBranch ? 'services' : 'branch');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Which gateway takes the deposit. The first one the site lists unless the
  // customer picks another on the review step.
  const [depositGateway, setDepositGateway] = useState<DepositGatewayCode | null>(
    site.booking.deposit_gateways?.[0]?.gateway ?? null,
  );

  // A single-branch tenant should never see a chooser.
  useEffect(() => {
    if (singleBranch && draft.branchId == null) draft.setBranch(site.branches[0]!.id);
  }, [singleBranch, draft, site.branches]);

  const branch = useMemo(
    () => site.branches.find((b) => b.id === draft.branchId) ?? null,
    [site.branches, draft.branchId],
  );
  const timezone = branch?.timezone ?? 'UTC';

  // ── data ────────────────────────────────────────────────────────────────
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  /**
   * Which selection the `staff` list actually describes, or null while it is
   * unknown. The step rail is derived from state, so "we have not asked yet"
   * and "we asked and nobody can do this" must be distinguishable — treating
   * them alike is what silently skipped the specialist step.
   */
  const [staffFor, setStaffFor] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [daysLoading, setDaysLoading] = useState(false);
  const [slots, setSlots] = useState<Slots | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(() => todayInZone(timezone));

  const range = useVisibleRange(timezone, site.booking.max_advance_days);
  const serviceIds = draft.serviceIds();

  const describe = useCallback(
    (err: unknown): string => {
      if (err instanceof BookingApiError) {
        const key = errorKeyFor(err.code);
        return key === 'err_generic' && err.message ? err.message : t(key);
      }
      return t('err_generic');
    },
    [t],
  );

  // Catalogue, per branch. The site payload already carries it for a
  // single-location tenant, so that case renders without a round trip.
  useEffect(() => {
    if (draft.branchId == null) return;
    if (site.catalog && site.catalog.branch_id === draft.branchId) {
      setCatalog(site.catalog);
      setCatalogLoading(false);
      return;
    }
    let cancelled = false;
    setCatalogLoading(true);
    bookingApi
      .catalog(slug, draft.branchId, site.subsidiary_id)
      .then((data) => !cancelled && setCatalog(data))
      .catch((err) => !cancelled && setError(describe(err)))
      .finally(() => !cancelled && setCatalogLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug, draft.branchId, site.subsidiary_id, site.catalog, describe]);

  // Staff who can do everything selected.
  const serviceKey = serviceIds.join(',');
  useEffect(() => {
    if (draft.branchId == null || serviceIds.length === 0) {
      setStaff((prev) => (prev.length === 0 ? prev : []));
      setStaffFor(null);
      return;
    }
    let cancelled = false;
    setStaffLoading(true);
    setStaffFor(null);
    const key = `${draft.branchId}:${serviceKey}`;
    bookingApi
      .staff(slug, draft.branchId, serviceIds, site.subsidiary_id)
      .then((data) => {
        if (cancelled) return;
        setStaff(data);
        setStaffFor(key);
        // A specialist chosen for an earlier selection may not do the new one.
        if (draft.employeeId != null && !data.some((s) => s.id === draft.employeeId)) {
          draft.setEmployee(null);
        }
      })
      .catch((err) => !cancelled && setError(describe(err)))
      .finally(() => !cancelled && setStaffLoading(false));
    return () => {
      cancelled = true;
    };
    // serviceIds is rebuilt each render; its join is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, draft.branchId, serviceKey, site.subsidiary_id, describe]);

  // Day availability for the visible window.
  useEffect(() => {
    if (step !== 'time' || draft.branchId == null || serviceIds.length === 0) return;
    let cancelled = false;
    setDaysLoading(true);
    bookingApi
      .availability(slug, {
        branchId: draft.branchId,
        serviceIds,
        employeeId: draft.employeeId,
        from: range.from,
        to: range.to,
        subsidiaryId: site.subsidiary_id,
      })
      .then((data) => {
        if (cancelled) return;
        setAvailability(data);
        // Land the customer on the first day that actually has room.
        const firstOpen = data.days.find((d) => d.available);
        if (firstOpen && !data.days.some((d) => d.date === selectedDate && d.available)) {
          setSelectedDate(firstOpen.date);
        }
      })
      .catch((err) => !cancelled && setError(describe(err)))
      .finally(() => !cancelled && setDaysLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, slug, draft.branchId, draft.employeeId, serviceIds.join(','), range.from, range.to]);

  // Slots for the chosen day.
  useEffect(() => {
    if (step !== 'time' || draft.branchId == null || serviceIds.length === 0) return;
    let cancelled = false;
    setSlotsLoading(true);
    bookingApi
      .slots(slug, {
        branchId: draft.branchId,
        serviceIds,
        employeeId: draft.employeeId,
        date: selectedDate,
        subsidiaryId: site.subsidiary_id,
      })
      .then((data) => !cancelled && setSlots(data))
      .catch((err) => !cancelled && setError(describe(err)))
      .finally(() => !cancelled && setSlotsLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, slug, draft.branchId, draft.employeeId, serviceIds.join(','), selectedDate]);

  // ── navigation ──────────────────────────────────────────────────────────

  /**
   * Whether the staff list on hand describes the CURRENT branch + selection.
   * False while a fetch is in flight, which `buildSteps` needs in order to tell
   * "we have not asked yet" from "this salon has nobody" — see lib/steps.ts.
   */
  const staffResolved = staffFor === `${draft.branchId}:${serviceKey}`;

  const steps = useMemo(
    () =>
      buildSteps({
        singleBranch,
        branchChosen: draft.branchId != null,
        serviceCount: draft.services.length,
        allowStaffSelection: site.booking.allow_staff_selection,
        staffResolved,
        staffCount: staff.length,
        timeChosen: draft.startAt != null,
        labels: {
          branch: t('step_location'),
          services: t('step_services'),
          staff: t('step_staff'),
          time: t('step_time'),
          review: t('step_confirm'),
        },
      }),
    [
      singleBranch,
      site.booking.allow_staff_selection,
      staffResolved,
      staff.length,
      draft.branchId,
      draft.services.length,
      draft.startAt,
      t,
    ],
  );

  function goBack(): void {
    const target = previousStep(steps, step);
    if (target) setStep(target);
  }

  /**
   * Advance from the time step: reserve the slot, signing in first if needed.
   * Holding before the customer reviews is deliberate — it means the time they
   * are reading about is genuinely theirs while they read it.
   */
  const takeHold = useCallback(async (): Promise<void> => {
    if (draft.branchId == null || !draft.startAt) return;
    setBusy(true);
    setError(null);
    try {
      const hold = await bookingApi.createHold(slug, {
        branch_id: draft.branchId,
        service_ids: draft.serviceIds(),
        employee_id: draft.employeeId,
        start_at: draft.startAt,
        subsidiary_id: site.subsidiary_id,
      });
      draft.setHold(hold);
      setStep('review');
    } catch (err) {
      setError(describe(err));
      if (err instanceof BookingApiError && err.code === 'SLOT_UNAVAILABLE') {
        // Refresh the day so the taken slot disappears rather than being
        // offered again.
        setSlots(null);
        setStep('time');
      }
    } finally {
      setBusy(false);
    }
  }, [slug, draft, site.subsidiary_id, describe]);

  // Once the customer signs in with a time already chosen, continue for them.
  const [pendingHold, setPendingHold] = useState(false);
  useEffect(() => {
    if (pendingHold && account) {
      setPendingHold(false);
      void takeHold();
    }
  }, [pendingHold, account, takeHold]);

  function continueFromTime(): void {
    if (!account) {
      setPendingHold(true);
      setAuthOpen(true);
      return;
    }
    void takeHold();
  }

  async function confirm(): Promise<void> {
    if (!draft.hold) return;
    setBusy(true);
    setError(null);
    try {
      const result = await bookingApi.confirm(slug, {
        hold_token: draft.hold.token,
        ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
        locale: lang,
        return_url: portalReturnUrl(route),
        ...(depositGateway ? { gateway: depositGateway } : {}),
      });
      if (result.requires_payment && result.payment) {
        // Remember where to come back to; the return screen polls this charge.
        sessionStorage.setItem(`booking:${slug}:charge`, result.payment.charge_id);
        sessionStorage.setItem(`booking:${slug}:charge-ref`, result.payment.reference);
        window.location.assign(result.payment.redirect_url);
        return;
      }
      if (result.booking) {
        draft.reset();
        onBooked(result.booking);
      }
    } catch (err) {
      setError(describe(err));
      if (err instanceof BookingApiError && ['HOLD_EXPIRED', 'SLOT_UNAVAILABLE'].includes(err.code)) {
        draft.setHold(null);
        setStep('time');
      }
    } finally {
      setBusy(false);
    }
  }

  // ── render ──────────────────────────────────────────────────────────────

  const canContinue =
    step === 'branch'
      ? draft.branchId != null
      : step === 'services'
        ? draft.services.length > 0
        : step === 'staff'
          ? // Not while the list is still arriving: continuing then would quietly
            // fall through to "any available" before the customer saw the choice.
            !staffLoading
          : step === 'time'
            ? draft.startAt != null
            : false;

  function next(): void {
    if (step === 'time') {
      continueFromTime();
      return;
    }
    const target = nextStep(steps, step);
    if (target) setStep(target);
  }

  return (
    <div>
      <StepRail
        steps={steps}
        current={step}
        onJump={(key) => setStep(key as Step)}
      />

      {error && <ErrorNote message={error} />}

      {step === 'branch' && (
        <BranchStep
          branches={site.branches}
          selectedId={draft.branchId}
          onSelect={(id) => {
            draft.setBranch(id);
            setStep('services');
          }}
        />
      )}

      {step === 'services' && (
        <ServicesStep
          catalog={catalog}
          loading={catalogLoading}
          selected={draft.services}
          maxServices={site.booking.max_services_per_booking}
          onToggle={(service) => draft.toggleService(service, site.booking.max_services_per_booking)}
        />
      )}

      {step === 'staff' && (
        <StaffStep
          staff={staff}
          loading={staffLoading}
          selectedId={draft.employeeId}
          allowAny={site.booking.allow_any_staff}
          onSelect={(id) => {
            draft.setEmployee(id);
            setStep('time');
          }}
        />
      )}

      {step === 'time' && (
        <TimeStep
          availability={availability}
          slots={slots}
          loadingDays={daysLoading}
          loadingSlots={slotsLoading}
          selectedDate={selectedDate}
          selectedStart={draft.startAt}
          onPickDate={(date) => setSelectedDate(date)}
          onPickSlot={(slot: Slot) => draft.setSlot(slot.start_at, selectedDate, slot.local_time)}
          onExtendRange={range.extend}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          branch={branch}
          services={draft.services}
          staff={staff.find((s) => s.id === (draft.hold?.employee_id ?? draft.employeeId)) ?? null}
          hold={draft.hold}
          notes={draft.notes}
          depositRequired={draft.hold?.deposit_required ?? 0}
          gateways={site.booking.deposit_gateways ?? []}
          gateway={depositGateway}
          onGatewayChange={setDepositGateway}
          onNotesChange={draft.setNotes}
          onConfirm={() => void confirm()}
          onHoldExpired={() => undefined}
          busy={busy}
        />
      )}

      {step !== 'review' && (
        <div className="bk-summary mt-6 pt-3">
          {/*
            The running total is allowed to shrink and truncate; the two
            buttons are not. On a 320px screen a Back and a Continue at their
            natural width leave very little room for the summary line, so it
            gets `min-w-0` + truncation rather than wrapping the buttons onto a
            second row and pushing the page content up.
          */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="min-w-0 flex-1">
              {draft.services.length > 0 ? (
                <>
                  <p className="m-0 truncate text-sm font-medium text-zinc-900">
                    {draft.services.length} {t('selected')} ·{' '}
                    {formatDuration(draft.totalDuration(), lang)}
                  </p>
                  <p className="m-0 truncate text-xs text-zinc-500">
                    {money(draft.totalPrice())}
                    {draft.localTime ? ` · ${draft.localTime}` : ''}
                  </p>
                </>
              ) : (
                <p className="m-0 line-clamp-2 text-xs text-zinc-400">{t('choose_services_sub')}</p>
              )}
            </div>
            {steps.findIndex((s) => s.key === step) > 0 && (
              <button className="bk-btn bk-btn-ghost shrink-0 px-3 sm:px-4" onClick={goBack} disabled={busy}>
                {t('back')}
              </button>
            )}
            <button
              className="bk-btn bk-btn-primary shrink-0 px-4 sm:px-6"
              disabled={!canContinue || busy}
              onClick={next}
            >
              {busy ? t('loading') : t('continue')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Post-booking confirmation. */
export function BookedScreen({
  booking,
  onBookAnother,
  onViewBookings,
}: {
  booking: Booking;
  onBookAnother: () => void;
  onViewBookings: () => void;
}): JSX.Element {
  const { t } = useT();
  return (
    <div className="bk-rise">
      <div className="mb-6 text-center">
        <span
          className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
          aria-hidden
        >
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <ScreenHeading title={t('booked_title')} subtitle={t('booked_sub')} />
      </div>

      <BookingCard booking={booking} />

      <div className="mt-5 grid gap-2 sm:grid-cols-3">
        <a className="bk-btn bk-btn-ghost" href={calendarHref(booking)} download={`${booking.reference}.ics`}>
          {t('add_to_calendar')}
        </a>
        <button className="bk-btn bk-btn-ghost" onClick={onViewBookings}>
          {t('my_bookings')}
        </button>
        <button className="bk-btn bk-btn-primary" onClick={onBookAnother}>
          {t('book_another')}
        </button>
      </div>
    </div>
  );
}
